import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { TransactionWrapper, Upsell } from '@core/models';
import { TransactionService } from '@core/services/transaction.service';
import { environment } from '@environments/environment';
import { UpgradesService } from '@features/pages/select-seat/viewer/topbar/upgrades/upgrades.service';

@Component({
  selector: 'app-summary',
  imports: [CommonModule],
  templateUrl: './summary.component.html',
  styleUrl: './summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryComponent {
  private upgradesService = inject(UpgradesService);
  summaryText = input.required<string | null>();
  upsellSelected = input.required<Upsell | null>();
  transactionService = inject(TransactionService);
  rainoutValue = input.required<boolean>();
  totalTransactionSeats = input.required<number>();
  transaction = input.required<TransactionWrapper>();
  isTicketFeeEnabled = input.required<boolean>();
  isProduction = signal(environment.production);

  get serviceChargesDetails() {
    return this.transactionService.getServiceChargesDetails(
      this.transaction().transaction.tdcTransaction!
    );
  }

  get actualServiceCharges() {
    return (
      (this.transaction().transaction?.tdcTransaction?.balance || 0) -
      (this.transaction().transaction?.tdcTransaction?.totalPriceSeats ?? 0)
    );
  }

  get actualServiceChargesAmountWithoutInsuranceAndUpsell() {
    let amount =
      this.actualServiceCharges -
      (this.transaction().transaction?.tdcTransaction?.insuranceRainoutPrice ||
        0);

    const transactionUpsell = this.transactionService.getTransactionUpsell();
    if (transactionUpsell) {
      const transactionUpsellPrice =
        (transactionUpsell.price ?? 0) * this.totalTransactionSeats();
      amount = amount - transactionUpsellPrice;
    }

    return amount > 0 ? amount : 0;
  }

  get acquiredUpgrades() {
    return this.upgradesService.acquiredUpgrades;
  }

  get seats() {
    const map = new Map<
      number,
      { price: number; quantity: number; total: number }
    >();

    for (const seat of Object.values(
      this.transaction()?.transaction?.seatsPrices || {}
    )) {
      if (map.has(seat.value)) {
        map.get(seat.value)!.quantity += 1;
        map.get(seat.value)!.total += seat.value;
      } else {
        map.set(seat.value, {
          price: seat.value,
          quantity: 1,
          total: seat.value,
        });
      }
    }

    return Array.from(map.values());
  }

  get totalPriceSeats() {
    const transactionUpsell = this.transactionService.getTransactionUpsell();
    if (transactionUpsell) {
      const totalUpsellPrice =
        (transactionUpsell.price ?? 0) *
        this.transactionService.getTotalSeats();
      return (
        this.transaction().transaction!.tdcTransaction?.totalPriceSeats! -
        totalUpsellPrice
      );
    } else {
      return this.transaction().transaction!.tdcTransaction?.totalPriceSeats;
    }
  }
}
